import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import Vapi from '@vapi-ai/web';
import { toast } from 'sonner';

const VapiContext = createContext(null);

export const useVapi = () => {
  const context = useContext(VapiContext);
  if (!context) {
    throw new Error('useVapi must be used within VapiProvider');
  }
  return context;
};

export const VapiProvider = ({ children }) => {
  // Widget state
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('chat'); // 'chat' | 'voice'
  const [unreadCount, setUnreadCount] = useState(0);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [isAssistantTyping, setIsAssistantTyping] = useState(false);

  // Voice state
  const [callStatus, setCallStatus] = useState('idle'); // 'idle' | 'connecting' | 'active' | 'ended'
  const [isMuted, setIsMuted] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [callDuration, setCallDuration] = useState(0);

  // Vapi client ref
  const vapiClientRef = useRef(null);
  const callTimerRef = useRef(null);

  // Initialize Vapi client
  useEffect(() => {
    try {
      const publicKey = process.env.REACT_APP_VAPI_PUBLIC_KEY;

      if (!publicKey) {
        console.error('VAPI_PUBLIC_KEY not found in environment variables');
        return;
      }

      const vapi = new Vapi(publicKey);
      vapiClientRef.current = vapi;

      // Set up event listeners
      vapi.on('message', handleMessage);
      vapi.on('call-start', handleCallStart);
      vapi.on('call-end', handleCallEnd);
      vapi.on('speech-start', handleSpeechStart);
      vapi.on('speech-end', handleSpeechEnd);
      vapi.on('volume-level', handleVolumeLevel);
      vapi.on('error', handleError);

      // Cleanup on unmount
      return () => {
        if (vapi) {
          vapi.stop();
          if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
          }
        }
      };
    } catch (error) {
      console.error('Failed to initialize Vapi client:', error);
      toast.error('Failed to initialize chat assistant');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Event handlers
  const handleMessage = useCallback((message) => {
    console.log('Message received:', message);

    if (message.role === 'assistant') {
      const newMessage = {
        role: 'assistant',
        content: message.content || message.text || '',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, newMessage]);
      setIsAssistantTyping(false);

      // Increment unread count if widget is closed
      if (!isOpen) {
        setUnreadCount(prev => prev + 1);
      }
    }
  }, [isOpen]);

  const handleCallStart = useCallback(() => {
    console.log('Call started');
    setCallStatus('active');
    toast.success('Voice call connected');

    // Start call timer
    setCallDuration(0);
    callTimerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  }, []);

  const handleCallEnd = useCallback(() => {
    console.log('Call ended');
    setCallStatus('ended');
    setIsMuted(false);
    toast.info('Voice call ended');

    // Stop call timer
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }

    // Reset to idle after a short delay
    setTimeout(() => {
      setCallStatus('idle');
      setCallDuration(0);
      setTranscript([]);
    }, 2000);
  }, []);

  const handleSpeechStart = useCallback(() => {
    console.log('Speech started');
    // Could add visual indicator for speech activity
  }, []);

  const handleSpeechEnd = useCallback(() => {
    console.log('Speech ended');
    // Could remove visual indicator for speech activity
  }, []);

  const handleVolumeLevel = useCallback((volume) => {
    // Could use this for visual audio level indicator
    // console.log('Volume level:', volume);
  }, []);

  const handleError = useCallback((error) => {
    console.error('Vapi error:', error);

    let errorMessage = 'An error occurred';

    if (error.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    toast.error(errorMessage);
    setIsAssistantTyping(false);

    if (callStatus !== 'idle') {
      setCallStatus('ended');
      setTimeout(() => setCallStatus('idle'), 2000);
    }
  }, [callStatus]);

  // Widget actions
  const openWidget = useCallback(() => {
    setIsOpen(true);
    setUnreadCount(0);
  }, []);

  const closeWidget = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleMode = useCallback((newMode) => {
    if (callStatus === 'active' || callStatus === 'connecting') {
      toast.warning('Please end the current call before switching modes');
      return;
    }
    setMode(newMode);
  }, [callStatus]);

  // Chat actions
  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || !vapiClientRef.current) return;

    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsAssistantTyping(true);

    try {
      await vapiClientRef.current.send({
        type: 'add-message',
        message: {
          role: 'user',
          content: text
        }
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message. Please try again.');
      setIsAssistantTyping(false);
    }
  }, []);

  // Voice actions
  const startCall = useCallback(async () => {
    if (!vapiClientRef.current) {
      toast.error('Voice assistant not available');
      return;
    }

    const assistantId = process.env.REACT_APP_VAPI_ASSISTANT_ID;

    if (!assistantId) {
      toast.error('Assistant ID not configured');
      return;
    }

    try {
      setCallStatus('connecting');
      setTranscript([]);
      setCallDuration(0);

      await vapiClientRef.current.start(assistantId);
    } catch (error) {
      console.error('Failed to start call:', error);

      let errorMessage = 'Failed to start voice call';

      if (error.message && error.message.includes('permission')) {
        errorMessage = 'Microphone permission denied. Please enable microphone access in your browser settings.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
      setCallStatus('idle');
    }
  }, []);

  const endCall = useCallback(() => {
    if (vapiClientRef.current) {
      vapiClientRef.current.stop();
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (!vapiClientRef.current) return;

    const newMutedState = !isMuted;
    setIsMuted(newMutedState);

    try {
      vapiClientRef.current.setMuted(newMutedState);
      toast.info(newMutedState ? 'Microphone muted' : 'Microphone unmuted');
    } catch (error) {
      console.error('Failed to toggle mute:', error);
      setIsMuted(!newMutedState); // Revert on error
    }
  }, [isMuted]);

  // Format call duration as MM:SS
  const formattedDuration = React.useMemo(() => {
    const minutes = Math.floor(callDuration / 60);
    const seconds = callDuration % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [callDuration]);

  const value = {
    // Widget state
    isOpen,
    mode,
    unreadCount,

    // Chat state
    messages,
    isAssistantTyping,

    // Voice state
    callStatus,
    isMuted,
    transcript,
    callDuration,
    formattedDuration,

    // Actions
    openWidget,
    closeWidget,
    toggleMode,
    sendMessage,
    startCall,
    endCall,
    toggleMute
  };

  return (
    <VapiContext.Provider value={value}>
      {children}
    </VapiContext.Provider>
  );
};
